<script setup lang="ts">
import Button from "primevue/button";
import { computed } from "vue";
import { Participant, Project, Trainer } from "../../../types/PersologOnlineTypes.ts";
import useQuestionnaireSend from "../../../utils/buttons-logic/sendQuestionnaireLogic.ts";
import { useI18n } from "vue-i18n";
import { useToast } from "primevue/usetoast";

const props = defineProps({
    participant: {
        type: Object as () => Participant,
        required: true,
    },
    project: {
        type: Object as () => Project,
        required: true,
    },
    trainer: {
        type: Object as () => Trainer,
        required: true,
    },
    quantity: {
        type: Number,
        required: true,
    },
    withClasses: {
        type: String,
        default: "",
    },
});

const emit = defineEmits(["update:participant"]);

const { t } = useI18n();
const toast = useToast();

const { loading, activateOrRemind } = useQuestionnaireSend(
    computed(() => props.participant),
    computed(() => props.project),
    props.trainer,
);

async function handleClick() {
    try {
        const result = await activateOrRemind();
        if (result) {
            emit("update:participant", [result]);
        }
        toast.add({
            severity: "success",
            summary: t("project-details.participants.toasts.email.summary"),
            detail: t("project-details.participants.toasts.email.success"),
            life: 5000,
        });
    } catch (error) {
        console.error(error);
        toast.add({
            severity: "error",
            summary: t("project-details.participants.toasts.email.summary"),
            detail: t("project-details.participants.toasts.email.error"),
            life: 3000,
        });
    }
}
</script>

<template>
    <Button
        v-if="participant?.status === 'CREATED' && quantity > 0"
        :icon="loading ? 'pi pi-spin pi-spinner' : 'pi pi-send'"
        :disabled="loading"
        @click="handleClick"
        :class="withClasses"
        v-tooltip.top="{
            value: t('project-details.participants.tooltips.send'),
            showDelay: 500,
        }"
    ></Button>
    <Button
        v-if="participant?.status === 'ACTIVATED' || participant?.status === 'STARTED'"
        :icon="loading ? 'pi pi-spin pi-spinner' : 'pi pi-bell'"
        :disabled="loading"
        @click="handleClick"
        :class="withClasses"
        v-tooltip.top="{
            value: t('project-details.participant.tooltips.resend'),
            showDelay: 500,
        }"
    ></Button>
</template>
